import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Autocomplete,
  IconButton,
  Tooltip,
  TablePagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
} from '@mui/material';
import {
  CheckCircle as ResolveIcon,
  Cancel as IgnoreIcon,
  Add as AddIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { fleetSyncAPI, devicesAPI } from '../services/api';

function IMEIConflictsPage() {
  const [conflicts, setConflicts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('pending');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [orderBy, setOrderBy] = useState('detected_at');
  const [order, setOrder] = useState('desc');
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [resolveMode, setResolveMode] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedTab, page, rowsPerPage, searchQuery, reasonFilter, orderBy, order]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load paginated conflicts
      console.log('Loading IMEI conflicts with status:', selectedTab, 'page:', page + 1, 'rows:', rowsPerPage);
      
      const conflictsRes = await fleetSyncAPI.getIMEIConflicts({ 
        status: selectedTab,
        page: page + 1,
        page_size: rowsPerPage,
        search: searchQuery.trim() || undefined,
        reason: reasonFilter || undefined,
        ordering: order === 'asc' ? orderBy : `-${orderBy}`
      });
      
      console.log('Conflicts API response:', conflictsRes);
      console.log('Response data:', conflictsRes.data);
      
      const summaryRes = await fleetSyncAPI.getIMEIConflictsSummary();
      console.log('Summary response:', summaryRes);
      
      // Handle response structure
      let conflictsData = [];
      let totalCountValue = 0;
      
      if (conflictsRes && conflictsRes.data) {
        if (conflictsRes.data.results && Array.isArray(conflictsRes.data.results)) {
          // Paginated response
          conflictsData = conflictsRes.data.results;
          totalCountValue = conflictsRes.data.count || 0;
          console.log('Paginated results found, length:', conflictsData.length, 'total:', totalCountValue);
        } else if (Array.isArray(conflictsRes.data)) {
          // Direct array - implement client-side pagination (fallback)
          const allConflicts = conflictsRes.data;
          console.log('Direct array found, length:', allConflicts.length);
          
          // Client-side pagination
          const startIndex = page * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          conflictsData = allConflicts.slice(startIndex, endIndex);
          totalCountValue = allConflicts.length;
        } else {
          console.log('Unknown structure, keys:', Object.keys(conflictsRes.data));
        }
      }
      
      console.log('Final conflicts:', conflictsData.length);
      console.log('Total count:', totalCountValue);
      
      setConflicts(conflictsData);
      setTotalCount(totalCountValue);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Load error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.error || 'Failed to load IMEI conflicts');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDevices = async () => {
    try {
      const response = await devicesAPI.getAllBulk({ status: 'stock' });
      console.log('Devices response:', response);
      
      // Handle devices response - ensure it's an array
      let devicesArray = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          devicesArray = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          devicesArray = response.data.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          devicesArray = response.data.results;
        }
      }
      
      setAvailableDevices(devicesArray);
    } catch (err) {
      console.error('Failed to load devices:', err);
    }
  };

  const handleOpenResolveDialog = (conflict, mode) => {
    setSelectedConflict(conflict);
    setResolveMode(mode);
    setNote('');
    setSelectedDevice(null);
    
    if (mode === 'existing') {
      loadAvailableDevices();
    }
    
    setResolveDialogOpen(true);
  };

  const handleCloseResolveDialog = () => {
    setResolveDialogOpen(false);
    setSelectedConflict(null);
    setResolveMode(null);
    setSelectedDevice(null);
    setNote('');
  };

  const handleResolve = async () => {
    if (!selectedConflict) return;
    
    try {
      setSubmitting(true);
      
      if (resolveMode === 'existing') {
        if (!selectedDevice) {
          setError('Please select a device');
          return;
        }
        await fleetSyncAPI.resolveIMEIConflictWithExisting(selectedConflict.id, {
          device_id: selectedDevice.id,
          note,
        });
      } else if (resolveMode === 'new') {
        await fleetSyncAPI.resolveIMEIConflictWithNew(selectedConflict.id, { note });
      }
      
      handleCloseResolveDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resolve conflict');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIgnore = async (conflict) => {
    if (!window.confirm(`Ignore IMEI conflict for ${conflict.sync_imei}?`)) return;
    
    try {
      await fleetSyncAPI.ignoreIMEIConflict(conflict.id, { note: 'Ignored by user' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to ignore conflict');
    }
  };

  const handleReopen = async (conflict) => {
    if (!window.confirm(`Reopen IMEI conflict for ${conflict.sync_imei}?`)) return;
    
    try {
      await fleetSyncAPI.reopenIMEIConflict(conflict.id);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reopen conflict');
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0); // Reset to first page when clearing search
  };

  const handleReasonFilterChange = (event) => {
    setReasonFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handleClearReasonFilter = () => {
    setReasonFilter('');
    setPage(0); // Reset to first page when clearing filter
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'resolved_existing': return 'success';
      case 'resolved_new': return 'info';
      case 'ignored': return 'default';
      default: return 'default';
    }
  };

  const getConflictReasonDisplay = (reason) => {
    switch (reason) {
      case 'not_found': return 'Dispositif Non Trouvé';
      case 'inactive': return 'Dispositif Inactif';
      case 'maintenance': return 'Dispositif en Maintenance';
      case 'assigned': return 'Déjà Assigné';
      default: return 'Inconnu';
    }
  };

  const getConflictReasonColor = (reason) => {
    switch (reason) {
      case 'not_found': return 'error';
      case 'inactive': return 'secondary';
      case 'maintenance': return 'warning';
      case 'assigned': return 'info';
      default: return 'default';
    }
  };

  if (loading && !conflicts.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Conflits IMEI
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Total</Typography>
                <Typography variant="h4">{summary.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'warning.light' }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">En attente</Typography>
                <Typography variant="h4">{summary.pending}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Résolu (Existant)</Typography>
                <Typography variant="h4">{summary.resolved_existing}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'info.light' }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Résolu (Nouveau)</Typography>
                <Typography variant="h4">{summary.resolved_new}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Ignoré</Typography>
                <Typography variant="h4">{summary.ignored}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
          <Tab label="En attente" value="pending" />
          <Tab label="Résolu (Existant)" value="resolved_existing" />
          <Tab label="Résolu (Nouveau)" value="resolved_new" />
          <Tab label="Ignoré" value="ignored" />
        </Tabs>
      </Paper>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              sx={{width:"550px"}}
              placeholder="Rechercher par IMEI, modèle, véhicule, client, serveur..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl sx={{width:"250px"}}>
              <InputLabel>Filtrer par Raison</InputLabel>
              <Select
                value={reasonFilter}
                onChange={handleReasonFilterChange}
                label="Filtrer par Raison"
              >
                <MenuItem value="">Toutes les raisons</MenuItem>
                <MenuItem value="not_found">Dispositif Non Trouvé</MenuItem>
                <MenuItem value="inactive">Dispositif Inactif</MenuItem>
                <MenuItem value="maintenance">Dispositif en Maintenance</MenuItem>
                <MenuItem value="assigned">Déjà Assigné</MenuItem>
              </Select>
              {reasonFilter && (
                <Box sx={{ mt: 1 }}>
                  <Button 
                    size="small" 
                    onClick={handleClearReasonFilter}
                    startIcon={<ClearIcon />}
                  >
                    Effacer le filtre
                  </Button>
                </Box>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'sync_imei' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'sync_imei'}
                  direction={orderBy === 'sync_imei' ? order : 'asc'}
                  onClick={() => handleSort('sync_imei')}
                >
                  IMEI
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'server__name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'server__name'}
                  direction={orderBy === 'server__name' ? order : 'asc'}
                  onClick={() => handleSort('server__name')}
                >
                  Serveur
                </TableSortLabel>
              </TableCell>
              <TableCell>Modèle</TableCell>
              <TableCell>Véhicule</TableCell>
              <TableCell>Client</TableCell>
              <TableCell sortDirection={orderBy === 'reason' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'reason'}
                  direction={orderBy === 'reason' ? order : 'asc'}
                  onClick={() => handleSort('reason')}
                >
                  Raison du Conflit
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'detected_at' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'detected_at'}
                  direction={orderBy === 'detected_at' ? order : 'asc'}
                  onClick={() => handleSort('detected_at')}
                >
                  Détecté le
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conflicts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    Aucun conflit IMEI trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              conflicts.map((conflict) => (
                <TableRow key={conflict.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {conflict.sync_imei}
                    </Typography>
                  </TableCell>
                  <TableCell>{conflict.server_name}</TableCell>
                  <TableCell>{conflict.sync_device_data?.model || '-'}</TableCell>
                  <TableCell>{conflict.sync_vehicle_data?.name || '-'}</TableCell>
                  <TableCell>{conflict.sync_vehicle_data?.client_name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getConflictReasonDisplay(conflict.reason)}
                      color={getConflictReasonColor(conflict.reason)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={conflict.status_display}
                      color={getStatusColor(conflict.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(conflict.detected_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="right">
                    {conflict.status === 'pending' ? (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="Associer avec dispositif existant">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenResolveDialog(conflict, 'existing')}
                          >
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Créer nouveau dispositif">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleOpenResolveDialog(conflict, 'new')}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ignorer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleIgnore(conflict)}
                          >
                            <IgnoreIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => handleReopen(conflict)}
                      >
                        Rouvrir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />

      <Dialog open={resolveDialogOpen} onClose={handleCloseResolveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resolveMode === 'existing' ? 'Associer avec dispositif existant' : 'Créer nouveau dispositif'}
        </DialogTitle>
        <DialogContent>
          {selectedConflict && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                IMEI: <strong>{selectedConflict.sync_imei}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Modèle: {selectedConflict.sync_device_data?.model || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Véhicule: {selectedConflict.sync_vehicle_data?.name || '-'}
              </Typography>

              {resolveMode === 'existing' && (
                <Autocomplete
                  sx={{ mt: 3 }}
                  options={availableDevices}
                  getOptionLabel={(option) => `${option.imei} - ${option.model}`}
                  value={selectedDevice}
                  onChange={(e, value) => setSelectedDevice(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sélectionner un dispositif"
                      required
                    />
                  )}
                />
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Note (optionnel)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResolveDialog} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleResolve}
            variant="contained"
            disabled={submitting || (resolveMode === 'existing' && !selectedDevice)}
            startIcon={submitting ? <CircularProgress size={20} /> : <ResolveIcon />}
          >
            {resolveMode === 'existing' ? 'Associer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default IMEIConflictsPage;
